import React, { useState, useEffect } from 'react';

interface ApiUsage {
  totalSearches: number;
  totalApiCalls: number;
  apiStatus: 'live' | 'rate_limit';
  lastRateLimitError: string | null;
  lastUpdated: string;
}

interface QueryLog {
  _id: string;
  query: string;
  timestamp: string;
  apiCallsMade: number;
  resultCount: number;
  success: boolean;
  errorMessage?: string;
  isRateLimited?: boolean;
}


interface ApiKeyInfo {
  envKeyMasked: string;
  fallbackKeyMasked: string;
  activeSource: 'env' | 'fallback';
  hasEnvKey: boolean;
  hasFallbackKey: boolean;
}

export const DevPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'keys' | 'logs' | 'settings'>('overview');
  const [usage, setUsage] = useState<ApiUsage | null>(null);
  const [logs, setLogs] = useState<QueryLog[]>([]);
  const [apiKeyInfo, setApiKeyInfo] = useState<ApiKeyInfo | null>(null);
  const [newFallbackKey, setNewFallbackKey] = useState('');
  const [oldPin, setOldPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usageRes, logsRes, keyRes] = await Promise.all([
        fetch('/api/dev/usage'),
        fetch('/api/dev/logs'),
        fetch('/api/dev/api-key')
      ]);

      const usageData = await usageRes.json();
      const logsData = await logsRes.json();
      const keyData = await keyRes.json();

      if (usageData.success) setUsage(usageData.data);
      if (logsData.success) setLogs(logsData.data);
      if (keyData.success) setApiKeyInfo(keyData.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSetFallbackKey = async () => {
    if (!newFallbackKey.trim()) {
      setMessage({ type: 'error', text: 'Please enter a fallback API key' });
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch('/api/dev/fallback-key', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: newFallbackKey }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: 'Fallback API key saved successfully' });
        setNewFallbackKey('');
        fetchData();
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to save fallback key' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving API key' });
    } finally {
      setSaving(false);
    }
  };

  const handleSourceChange = async (source: 'env' | 'fallback') => {
    setSaving(true);
    try {
      const response = await fetch('/api/dev/active-source', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source }),
      });
      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: `Switched to ${source === 'env' ? 'Environment' : 'Fallback'} Key` });
        fetchData();
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to switch key source' });
    } finally {
      setSaving(false);
    }
  };


  const handleChangePin = async () => {
    if (!oldPin || !newPin || !confirmPin) {
      setMessage({ type: 'error', text: 'Please fill in all PIN fields' });
      return;
    }

    if (newPin !== confirmPin) {
      setMessage({ type: 'error', text: 'New PIN and confirmation do not match' });
      return;
    }

    if (newPin.length < 4) {
      setMessage({ type: 'error', text: 'New PIN must be at least 4 characters' });
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch('/api/dev/change-pin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ oldPin, newPin }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: 'Site Access PIN changed successfully.' });
        setOldPin('');
        setNewPin('');
        setConfirmPin('');
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to change PIN' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error changing PIN' });
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = () => {
    setMessage({ type: '', text: '' });
    fetchData();
  };

  const handleLogout = () => {
    sessionStorage.removeItem('dev_auth');
    window.location.href = '/dev';
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString();
  };

  const maskApiKey = (key: string) => {
    if (key.length <= 8) return '••••••••';
    return key.substring(0, 4) + '••••••••' + key.substring(key.length - 4);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center border border-blue-200">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Developer Panel</h1>
                <p className="text-sm text-gray-500">API Management & Monitoring</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors"
              >
                ← Main App
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-500 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-2 mb-6 bg-white p-1 rounded-lg border border-gray-200 inline-flex">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
              activeTab === 'overview'
                ? 'bg-blue-50 text-blue-700 shadow-sm'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('keys')}
            className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
              activeTab === 'keys'
                ? 'bg-blue-50 text-blue-700 shadow-sm'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            API Keys
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
              activeTab === 'logs'
                ? 'bg-blue-50 text-blue-700 shadow-sm'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Query Logs
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
              activeTab === 'settings'
                ? 'bg-blue-50 text-blue-700 shadow-sm'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Settings
          </button>
        </div>

        {/* Messages */}
        {message.text && (
          <div className={`mb-6 px-4 py-3 rounded-lg border ${
            message.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-700'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}>
            {message.text}
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-500 mt-4">Loading...</p>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && usage && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-gray-500 text-sm font-medium">Total Searches</h3>
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{usage.totalSearches}</p>
                    <p className="text-xs text-gray-500 mt-1">Unique queries performed</p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-gray-500 text-sm font-medium">API Calls</h3>
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </div>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{usage.totalApiCalls}</p>
                    <p className="text-xs text-gray-500 mt-1">Serper API requests made</p>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                  <h3 className="text-gray-900 text-lg font-semibold mb-4">API Status</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${usage.apiStatus === 'live' ? 'bg-green-500 animate-pulse' : 'bg-red-500 animate-pulse'}`}></div>
                      <div>
                        <p className="text-sm text-gray-500">Current Status</p>
                        <p className={`text-lg font-semibold ${usage.apiStatus === 'live' ? 'text-green-600' : 'text-red-600'}`}>
                          {usage.apiStatus === 'live' ? 'Live' : 'Rate Limited'}
                        </p>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Last Updated</p>
                      <p className="text-gray-900">{formatDate(usage.lastUpdated)}</p>
                    </div>
                  </div>
                  {usage.apiStatus === 'rate_limit' && usage.lastRateLimitError && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-700 text-sm font-medium">Rate Limit Detected</p>
                      <p className="text-red-600 text-xs mt-1">
                        Last occurred: {formatDate(usage.lastRateLimitError)}
                      </p>
                      <p className="text-red-600 text-xs mt-2">
                        API quota may be exhausted. Check your Serper.dev account or wait for quota reset.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* API Keys Tab */}
            {activeTab === 'keys' && (
              <div className="space-y-6">
                
                {/* Active Source Selection */}
                <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                  <h3 className="text-gray-900 text-lg font-semibold mb-4">Active Key Source</h3>
                  <div className="flex gap-4">
                    <button
                      onClick={() => handleSourceChange('env')}
                      className={`flex-1 p-4 rounded-xl border-2 transition-all text-left relative ${
                        apiKeyInfo?.activeSource === 'env'
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={`font-semibold ${apiKeyInfo?.activeSource === 'env' ? 'text-blue-900' : 'text-gray-900'}`}>
                          Environment Variable
                        </span>
                        {apiKeyInfo?.activeSource === 'env' && (
                          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mb-2">Use key from .env file</p>
                      {apiKeyInfo?.hasEnvKey ? (
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600 block w-fit">
                          {apiKeyInfo.envKeyMasked}
                        </code>
                      ) : (
                        <span className="text-xs text-red-500 font-medium">Not Found</span>
                      )}
                    </button>

                    <button
                      onClick={() => handleSourceChange('fallback')}
                      className={`flex-1 p-4 rounded-xl border-2 transition-all text-left relative ${
                        apiKeyInfo?.activeSource === 'fallback'
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={`font-semibold ${apiKeyInfo?.activeSource === 'fallback' ? 'text-blue-900' : 'text-gray-900'}`}>
                          Fallback Database Key
                        </span>
                        {apiKeyInfo?.activeSource === 'fallback' && (
                          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mb-2">Use key stored in database</p>
                      {apiKeyInfo?.hasFallbackKey ? (
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600 block w-fit">
                          {apiKeyInfo.fallbackKeyMasked}
                        </code>
                      ) : (
                        <span className="text-xs text-orange-500 font-medium">Not Configured</span>
                      )}
                    </button>
                  </div>
                </div>

                {/* Set Fallback Key */}
                <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                  <h3 className="text-gray-900 text-lg font-semibold mb-4">Manage Fallback Key</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-gray-700 text-sm mb-2">
                        Set Fallback API Key
                      </label>
                      <input
                        type="password"
                        value={newFallbackKey}
                        onChange={(e) => setNewFallbackKey(e.target.value)}
                        placeholder="Enter fallback API key"
                        className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                      />
                    </div>
                    <button
                      onClick={handleSetFallbackKey}
                      disabled={saving || !newFallbackKey.trim()}
                      className={`w-full py-3 rounded-lg font-medium transition-all ${
                        saving || !newFallbackKey.trim()
                          ? 'bg-gray-200 cursor-not-allowed text-gray-400'
                          : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-200'
                      }`}
                    >
                      {saving ? 'Saving...' : 'Set Fallback Key'}
                    </button>
                    <p className="text-xs text-gray-500">
                      This key acts as a backup system. Switch to "Fallback Database Key" above to use it.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Query Logs Tab */}
            {activeTab === 'logs' && (
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-gray-900 text-lg font-semibold">Query History</h3>
                  <p className="text-gray-500 text-sm mt-1">Recent search queries and API usage</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Query
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Timestamp
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          API Calls
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Results
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {logs.length > 0 ? (
                        logs.map((log) => (
                          <tr key={log._id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4 text-sm text-gray-900">
                              {log.query}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {formatDate(log.timestamp)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {log.apiCallsMade}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {log.resultCount}
                            </td>
                            <td className="px-6 py-4 text-sm">
                              {log.isRateLimited ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                                  Rate Limited
                                </span>
                              ) : log.success ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                  Success
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                                  Failed
                                </span>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                            No query logs found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <h3 className="text-gray-900 text-lg font-semibold mb-4">Change Site Access PIN</h3>
                <p className="text-gray-500 text-sm mb-6">
                  Update the PIN required for general site access. Check .env file to update Developer Panel PIN.
                </p>
                <div className="space-y-4 max-w-md">
                  <div>
                    <label className="block text-gray-700 text-sm mb-2">
                      Current Site PIN
                    </label>
                    <input
                      type="password"
                      value={oldPin}
                      onChange={(e) => setOldPin(e.target.value)}
                      placeholder="Enter current Site PIN"
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm mb-2">
                      New Site PIN
                    </label>
                    <input
                      type="password"
                      value={newPin}
                      onChange={(e) => setNewPin(e.target.value)}
                      placeholder="Enter new Site PIN (min 4 characters)"
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm mb-2">
                      Confirm New Site PIN
                    </label>
                    <input
                      type="password"
                      value={confirmPin}
                      onChange={(e) => setConfirmPin(e.target.value)}
                      placeholder="Confirm new Site PIN"
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                    />
                  </div>
                  <button
                    onClick={handleChangePin}
                    disabled={saving || !oldPin || !newPin || !confirmPin}
                    className={`w-full py-3 rounded-lg font-medium transition-all ${
                      saving || !oldPin || !newPin || !confirmPin
                        ? 'bg-gray-200 cursor-not-allowed text-gray-400'
                        : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-200'
                    }`}
                  >
                    {saving ? 'Changing PIN...' : 'Change Site Access PIN'}
                  </button>
                  <p className="text-xs text-gray-500">
                    <strong>Note:</strong> This changes the PIN for the main welcome screen, not the developer panel.
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
